/**
 * Utilitário para geração de BR Code (Pix Estático) padrão EMVCo.
 * Permite renderizar QRCodes nativos sem depender de plataformas de pagamento de terceiros.
 *
 * Referência normativa: Manual de Padrões para Iniciação do Pix (BCB, v2.9.0):
 * - §2.5.1 Formatação das chaves do DICT no BR Code (CPF/CNPJ só dígitos,
 *   telefone no formato internacional +55DDNNNNNNNNN, e-mail, EVP com hífens).
 * - §2.6.2 txid (EMV 62-05): máx. 25 caracteres, somente [a-zA-Z0-9]; sem
 *   identificador, usar '***'.
 * - EMV QRCPS-MPM: Merchant Name (59) máx. 25; Merchant City (60) máx. 15.
 */

// Formata campos no padrão EMVCo: ID + Tamanho (2 dígitos) + Valor
function formatField(id: string, value: string): string {
  const size = value.length.toString().padStart(2, '0');
  return `${id}${size}${value}`;
}

// Remove acentos e caracteres especiais indesejados no PIX
function sanitizeStr(str: string, maxLen: number): string {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9 ]/g, '')
    .substring(0, maxLen)
    .trim();
}

// txid (EMV 62-05): o BCB só permite letras e dígitos, máx. 25. Fora disso o
// QR é rejeitado pelos validadores dos PSPs ("QR Code inválido").
function sanitizeTxid(txid: string): string {
  const clean = txid.replace(/[^a-zA-Z0-9]/g, '').substring(0, 25);
  return clean || '***';
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CPF_DISPLAY_RE = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/;
const CNPJ_DISPLAY_RE = /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/;

/**
 * Normaliza a chave Pix para o formato exigido pelo DICT (§2.5.1):
 * - CPF/CNPJ digitados com pontuação → somente dígitos;
 * - telefone com +55 ou com DDD entre parênteses → +55 e somente dígitos;
 * - e-mail → minúsculas;
 * - chave aleatória (EVP) → preservada com hífens.
 * Sequências ambíguas de 11 dígitos (CPF ou celular sem +55) são mantidas
 * como dígitos puros — formato correto para CPF, que é o caso predominante.
 */
export function normalizePixKey(pixKey: string): string {
  const key = pixKey.trim();

  if (key.includes('@')) return key.toLowerCase(); // e-mail
  if (UUID_RE.test(key)) return key; // chave aleatória: mantém pontuação

  // Telefone: exige o formato internacional +55DDNNNNNNNNN
  if (key.startsWith('+')) return `+${key.replace(/\D/g, '')}`;
  if (key.includes('(')) return `+55${key.replace(/\D/g, '')}`;

  // CPF/CNPJ com máscara → só dígitos
  if (CPF_DISPLAY_RE.test(key) || CNPJ_DISPLAY_RE.test(key)) return key.replace(/\D/g, '');

  // Dígitos com separadores soltos (ex.: "123 456 789 00") → só dígitos
  const digits = key.replace(/[\s./-]/g, '');
  if (/^\d+$/.test(digits) && (digits.length === 11 || digits.length === 14)) {
    return digits;
  }

  return key;
}

// -- Validação guiada por tipo (UX do cadastro da chave) -------------------------

export type PixKeyType = 'cpf' | 'cnpj' | 'celular' | 'email' | 'aleatoria';

export interface PixKeyValidation {
  valid: boolean;
  /** Mensagem em linguagem simples para o usuário (presente quando inválida). */
  message?: string;
  /** Chave no formato DICT, pronta para salvar (presente quando válida). */
  normalized?: string;
}

/** Infere o tipo da chave a partir de um valor já salvo (para o seletor da UI). */
export function inferPixKeyType(key: string): PixKeyType {
  const k = key.trim();
  if (!k) return 'cpf';
  if (k.includes('@')) return 'email';
  if (UUID_RE.test(k)) return 'aleatoria';
  if (k.startsWith('+')) return 'celular';
  const digits = k.replace(/\D/g, '');
  if (digits.length === 14 || /[a-zA-Z]/.test(k)) return 'cnpj';
  return 'cpf';
}

// Dígitos verificadores do CPF (módulo 11).
function cpfValido(cpf: string): boolean {
  if (!/^\d{11}$/.test(cpf) || /^(\d)\1{10}$/.test(cpf)) return false;
  for (const pos of [9, 10]) {
    let soma = 0;
    for (let i = 0; i < pos; i++) soma += parseInt(cpf[i], 10) * (pos + 1 - i);
    const dv = ((soma * 10) % 11) % 10;
    if (dv !== parseInt(cpf[pos], 10)) return false;
  }
  return true;
}

// Dígitos verificadores do CNPJ (módulo 11). Cobre também o CNPJ alfanumérico
// da RFB (vigente desde jul/2026): valor de cada caractere = ASCII - 48.
function cnpjValido(cnpj: string): boolean {
  const c = cnpj.toUpperCase();
  if (!/^[A-Z0-9]{12}\d{2}$/.test(c) || /^(.)\1{13}$/.test(c)) return false;
  const valor = (ch: string) => ch.charCodeAt(0) - 48;
  const pesos = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  for (const pos of [12, 13]) {
    let soma = 0;
    const base = pesos.slice(13 - pos);
    for (let i = 0; i < pos; i++) soma += valor(c[i]) * base[i];
    const resto = soma % 11;
    const dv = resto < 2 ? 0 : 11 - resto;
    if (dv !== parseInt(c[pos], 10)) return false;
  }
  return true;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Valida a chave digitada conforme o tipo escolhido, devolvendo mensagem em
 * linguagem simples e a forma normalizada (formato DICT, §2.5.1) para salvar.
 */
export function validatePixKey(type: PixKeyType, rawValue: string): PixKeyValidation {
  const raw = rawValue.trim();
  if (!raw) return { valid: false, message: 'Digite a chave.' };

  switch (type) {
    case 'cpf': {
      const digits = raw.replace(/\D/g, '');
      if (digits.length !== 11) {
        return { valid: false, message: 'O CPF tem 11 números. Confira o que faltou.' };
      }
      if (!cpfValido(digits)) {
        return { valid: false, message: 'Esse CPF parece incorreto. Confira os números.' };
      }
      return { valid: true, normalized: digits };
    }
    case 'cnpj': {
      const clean = raw.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      if (clean.length !== 14) {
        return { valid: false, message: 'O CNPJ tem 14 caracteres. Confira o que faltou.' };
      }
      if (!cnpjValido(clean)) {
        return { valid: false, message: 'Esse CNPJ parece incorreto. Confira os números.' };
      }
      return { valid: true, normalized: clean };
    }
    case 'celular': {
      const digits = raw.replace(/\D/g, '').replace(/^55(?=\d{11}$)/, '');
      if (digits.length !== 11) {
        return { valid: false, message: 'Digite o celular com DDD. Ex: (11) 91234-5678.' };
      }
      if (digits[2] !== '9') {
        return { valid: false, message: 'Depois do DDD, o celular começa com 9.' };
      }
      return { valid: true, normalized: `+55${digits}` };
    }
    case 'email': {
      if (!EMAIL_RE.test(raw)) {
        return { valid: false, message: 'Esse e-mail parece incompleto. Ex: nome@email.com.' };
      }
      return { valid: true, normalized: raw.toLowerCase() };
    }
    case 'aleatoria': {
      if (!UUID_RE.test(raw)) {
        return {
          valid: false,
          message: 'Chave aleatória não confere. Copie e cole do app do seu banco.',
        };
      }
      return { valid: true, normalized: raw.toLowerCase() };
    }
  }
}

// Calcula o CRC16 (CCITT-FALSE) exigido pelo BCB
function crc16(payload: string): string {
  let result = 0xffff;
  for (let i = 0; i < payload.length; i++) {
    result ^= payload.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) {
      if ((result & 0x8000) !== 0) {
        result = (result << 1) ^ 0x1021;
      } else {
        result = result << 1;
      }
    }
  }
  return (result & 0xffff).toString(16).toUpperCase().padStart(4, '0');
}

export function generatePixPayload(
  pixKey: string,
  // EMV 59/60 são obrigatórios na estrutura, mas o app do pagador exibe os
  // dados do DICT — o conteúdo é ignorado (manual BCB §2.6.3, nota 26). Os
  // defaults cobrem; passe o nome real da loja/usuário quando disponível.
  merchantName?: string,
  merchantCity: string = 'BRASILIA',
  amount?: number,
  txid: string = '***'
): string {
  // Normaliza a chave (formato DICT) e limpa os nomes
  const key = normalizePixKey(pixKey);
  const name = sanitizeStr(merchantName || 'NAO INFORMADO', 25) || 'NAO INFORMADO';
  // Merchant City (EMV 60) é limitado a 15 caracteres — acima disso o QR é inválido
  const city = sanitizeStr(merchantCity || 'BRASILIA', 15) || 'BRASILIA';

  // 00 - Payload Format Indicator (01)
  const payloadFormat = formatField('00', '01');

  // 26 - Merchant Account Info
  const gui = formatField('00', 'br.gov.bcb.pix');
  const pixKeyField = formatField('01', key);
  const merchantAccountInfo = formatField('26', gui + pixKeyField);

  // 52 - Merchant Category Code (0000 - não definido)
  const categoryCode = formatField('52', '0000');

  // 53 - Transaction Currency (986 - BRL)
  const currency = formatField('53', '986');

  // 54 - Transaction Amount (opcional)
  let amountField = '';
  if (amount && amount > 0) {
    amountField = formatField('54', amount.toFixed(2));
  }

  // 58 - Country Code (BR)
  const country = formatField('58', 'BR');

  // 59 - Merchant Name
  const merchant = formatField('59', name);

  // 60 - Merchant City
  const merchantCityField = formatField('60', city);

  // 62 - Additional Data Field Template (txid restrito a [a-zA-Z0-9], máx. 25)
  const txidField = formatField('05', sanitizeTxid(txid));
  const additionalData = formatField('62', txidField);

  // Monta a string inicial do Payload
  const payloadStr = `${payloadFormat}${merchantAccountInfo}${categoryCode}${currency}${amountField}${country}${merchant}${merchantCityField}${additionalData}`;

  // 63 - CRC16 (adiciona '6304' ao final para calcular o CRC)
  const payloadToCrc = payloadStr + '6304';
  const crc = crc16(payloadToCrc);

  // Retorna a Payload final com o CRC
  return `${payloadToCrc}${crc}`;
}
